import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Field, inputClass } from './BackgroundFields';
import { uid } from '../utils/defaults';

export default function DropdownOptionsFields({ options = [], sections = [], onChange }) {
  const list = options || [];

  const update = (next) => onChange(next);

  const addOption = () => {
    const firstAnchor = sections?.[0]?.anchor || 'accueil';
    update([
      ...list,
      {
        id: uid('opt'),
        label: 'Nouvelle option',
        linkType: 'section',
        sectionAnchor: firstAnchor,
        url: '',
      },
    ]);
  };

  const patchOption = (id, patch) => {
    update(list.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  };

  const removeOption = (id) => {
    update(list.filter((o) => o.id !== id));
  };

  const moveOption = (id, dir) => {
    const idx = list.findIndex((o) => o.id === id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const copy = [...list];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    update(copy);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-foreground">Options du menu</p>
        <Button type="button" variant="outline" size="sm" onClick={addOption}>
          <Icon name="Plus" size={14} className="mr-1" />
          Ajouter
        </Button>
      </div>

      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Ajoutez au moins une option pour alimenter le menu déroulant.
        </p>
      ) : (
        <ul className="space-y-3">
          {list.map((opt, index) => (
            <li
              key={opt.id}
              className="rounded-lg border border-border p-3 space-y-3 bg-muted/10"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Option {index + 1}
                </span>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    title="Monter"
                    disabled={index === 0}
                    onClick={() => moveOption(opt.id, -1)}
                  >
                    <Icon name="ChevronUp" size={14} />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    title="Descendre"
                    disabled={index === list.length - 1}
                    onClick={() => moveOption(opt.id, 1)}
                  >
                    <Icon name="ChevronDown" size={14} />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-destructive/10 text-destructive"
                    title="Supprimer"
                    onClick={() => removeOption(opt.id)}
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              </div>
              <Field label="Libellé">
                <input
                  className={inputClass}
                  value={opt.label || ''}
                  onChange={(e) => patchOption(opt.id, { label: e.target.value })}
                />
              </Field>
              <Field label="Action">
                <select
                  className={inputClass}
                  value={opt.linkType || 'section'}
                  onChange={(e) => patchOption(opt.id, { linkType: e.target.value })}
                >
                  <option value="section">Aller à une section</option>
                  <option value="url">Ouvrir une URL</option>
                </select>
              </Field>
              {opt.linkType === 'url' ? (
                <Field label="URL">
                  <input
                    className={inputClass}
                    value={opt.url || ''}
                    onChange={(e) => patchOption(opt.id, { url: e.target.value })}
                    placeholder="https://…"
                  />
                </Field>
              ) : (
                <Field label="Section cible">
                  <select
                    className={inputClass}
                    value={opt.sectionAnchor || ''}
                    onChange={(e) => patchOption(opt.id, { sectionAnchor: e.target.value })}
                  >
                    {(sections || []).map((s) => (
                      <option key={s.id} value={s.anchor}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
